import { ErrorMessage, useField } from 'formik';
import { parseDate } from '@internationalized/date';

import { CustomErrorMessage } from '@src/design-system/error-message';

import type { ComponentProps } from 'react';
import { cn } from '@src/design-system/helpers';
import {
  TiclickDatePicker,
  TiclickDatePickerCalendarPopover,
  TiclickDatePickerCalendarPopoverTrigger,
  TiclickDatePickerClearValue,
  TiclickDatePickerGroupAddon,
  TiclickDatePickerInput,
  TiclickDatePickerInputGroup,
  TiclickDatePickerLabel,
} from '@src/design-system/ticlick-date-picker';
import { CalendarIcon } from 'lucide-react';
import { CloseCircle } from 'iconsax-react';
import type { DatePicker } from 'react-aria-components';

type FormikDatePickerProps = {
  name: string;
  label?: string;
  closeOnSelect?: boolean;
};

export const FormikDatePicker = ({
  name,
  label,
  className,
  closeOnSelect = false,
  ...props
}: ComponentProps<typeof DatePicker> & FormikDatePickerProps) => {
  const [field, , helpers] = useField(name);

  const calenderDateValue = field.value
    ? parseDate(field.value.split('T')[0])
    : null;

  return (
    <div className={cn('w-full', className)}>
      <TiclickDatePicker
        value={calenderDateValue}
        onChange={(date) => helpers.setValue(date?.toString())}
        {...props}
        validationBehavior='aria'
      >
        {label && <TiclickDatePickerLabel>{label}</TiclickDatePickerLabel>}

        <TiclickDatePickerInputGroup>
          <TiclickDatePickerInput />

          <TiclickDatePickerGroupAddon
            className='flex items-center justify-center'
            align='end'
          >
            <TiclickDatePickerClearValue>
              <CloseCircle
                size={16}
                className='opacity-60 transition-colors hover:opacity-100 me-2'
              />
            </TiclickDatePickerClearValue>

            <TiclickDatePickerCalendarPopoverTrigger>
              <CalendarIcon size={16} />
            </TiclickDatePickerCalendarPopoverTrigger>

            <TiclickDatePickerCalendarPopover
              closeOnSelect={closeOnSelect}
              align='end'
            />
          </TiclickDatePickerGroupAddon>
        </TiclickDatePickerInputGroup>
      </TiclickDatePicker>

      <ErrorMessage
        name={field.name}
        component={CustomErrorMessage}
      />
    </div>
  );
};
